import { config } from '../config';

type TimeZoneParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
  millisecond: number;
};

const DEFAULT_TIME_ZONE_FALLBACK = 'America/New_York';

export const DEFAULT_TIME_ZONE = config.timeZone?.default ?? DEFAULT_TIME_ZONE_FALLBACK;

const timeZonePartsFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: DEFAULT_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

function getTimeZoneParts(date: Date, timeZone: string): TimeZoneParts {
  const formatter = timeZone === DEFAULT_TIME_ZONE
    ? timeZonePartsFormatter
    : new Intl.DateTimeFormat('en-US', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      });

  const parts = formatter.formatToParts(date);
  const partMap: Record<string, string> = {};

  for (const part of parts) {
    if (part.type !== 'literal') {
      partMap[part.type] = part.value;
    }
  }

  return {
    year: Number(partMap.year),
    month: Number(partMap.month),
    day: Number(partMap.day),
    hour: Number(partMap.hour),
    minute: Number(partMap.minute),
    second: Number(partMap.second),
    millisecond: date.getMilliseconds(),
  };
}

function getTimeZoneOffsetMinutes(utcDate: Date, timeZone: string): number {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    timeZoneName: 'shortOffset',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });

  const offsetPart = formatter
    .formatToParts(utcDate)
    .find((part) => part.type === 'timeZoneName')?.value;

  if (!offsetPart) {
    return 0;
  }

  const match = offsetPart.match(/GMT([+-]\d{1,2})(?::(\d{2}))?/);
  if (!match) {
    return 0;
  }

  const hours = Number(match[1]);
  const minutes = match[2] ? Number(match[2]) : 0;
  const sign = hours < 0 ? -1 : 1;

  return sign * (Math.abs(hours) * 60 + minutes);
}

function zonedPartsToUtcDate(parts: TimeZoneParts, timeZone: string): Date {
  const utcGuess = new Date(Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second,
    parts.millisecond
  ));

  const offsetMinutes = getTimeZoneOffsetMinutes(utcGuess, timeZone);
  return new Date(utcGuess.getTime() - offsetMinutes * 60000);
}

export function getStartOfDayInTimeZone(date: Date, timeZone = DEFAULT_TIME_ZONE): Date {
  const parts = getTimeZoneParts(date, timeZone);
  return zonedPartsToUtcDate({
    ...parts,
    hour: 0,
    minute: 0,
    second: 0,
    millisecond: 0,
  }, timeZone);
}

export function getStartOfNextDayInTimeZone(date: Date, timeZone = DEFAULT_TIME_ZONE): Date {
  const parts = getTimeZoneParts(date, timeZone);

  return zonedPartsToUtcDate({
    ...parts,
    day: parts.day + 1,
    hour: 0,
    minute: 0,
    second: 0,
    millisecond: 0,
  }, timeZone);
}

/**
 * Formats a UTC instant as its UTC calendar date (e.g. "Jan 1, 2026").
 * Use for date-only values stored as UTC midnight (license dates, period
 * boundaries) — formatting these in a local timezone shifts them a day back.
 */
export function formatUtcDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

export function formatShortDateInTimeZone(dateString: string, timeZone = DEFAULT_TIME_ZONE): string {
  const date = new Date(dateString);
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

  return formatter.format(date);
}
